import { DeleteNotePermanentlyModalStore } from "@/components/modal/delete-notes-permanently/delete-notes-permanently-modal-store";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { LeaveNoteMessageModalStore } from "@/store/pages/NotesViewPageStore/LeaveNoteMessageModalStore";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { Maybe } from "@/domains/common/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { createProxyWithIsDeleted } from "@/domains/react/useObjectAfterDeletion";
import { MountedStore } from "@/store/pages/MountedStore";
import { INoteObservable } from "@/store/note";
import { FileUploadRejectedModalStore } from "@/components/modal/file-upload-rejected";
import { ImageUploadRejectedModalStore } from "@/components/modal/image-upload-rejected";
import { PdfPageLimitWarningModalStore } from "@/components/modal/pdf-page-limit-warning/pdf-page-limit-warning-modal-store";
import { RenameFileModalStore } from "@/components/modal/rename-file";
import { AsyncResult } from "@/modules/async-result/types";
import { asyncResultModule } from "@/modules/async-result";
import { BaseError } from "@/domains/errors/kinds/BaseError";
import { objectModule } from "@/modules/object";
import { NoteVersionHistoryPageStore } from "./NoteVersionHistoryPageStore";
import { FindInNoteStore } from "@/store/pages/NotesViewPageStore/FindInNoteStore";

export class NotesViewPageStore
  extends AppSubStore
  implements INotesViewPageStore<INoteObservable>
{
  tabId: string;
  noteId: string;

  deleteNotePermanentlyModal: DeleteNotePermanentlyModalStore;
  findInNote: FindInNoteStore;
  shareSheetModal: ShareSheetModalStore;
  leaveNoteMessageModal: LeaveNoteMessageModalStore;
  addToCollectionModal: AddToCollectionModalStore;
  fileUploadRejectedModal: FileUploadRejectedModalStore;
  imageUploadRejectedModal: ImageUploadRejectedModalStore;
  pdfPageLimitWarningModal: PdfPageLimitWarningModalStore;
  renameFileModal: RenameFileModalStore;
  mountState = new MountedStore();

  // Page-scoped Note Version History store
  noteVersionHistory: NoteVersionHistoryPageStore;

  /** This is a proxy with isDeleted=true after the note is gone. */
  itemObservable: Maybe<INoteObservable>;
  couldAccessNoteOnMount: Maybe<boolean>;

  /** State tracking for note hydration when note doesn't exist locally */
  noteHydrationState: AsyncResult<boolean> = asyncResultModule.setInitial();
  currentNoteWasDeletedBySync = false;
  organizeMenuOpenRequestCount = 0;
  private lastNoteIdAddedToRecents: Maybe<string>;
  voiceCleanupPanelToggleRequestCount = 0;
  voiceCleanupChangesAcceptRequestCount = 0;

  constructor(injectedDeps: AppSubStoreArgs & { tabId: string; noteId: string }) {
    super(injectedDeps);
    this.tabId = injectedDeps.tabId;
    this.noteId = injectedDeps.noteId;

    this.findInNote = new FindInNoteStore(this);
    this.deleteNotePermanentlyModal = new DeleteNotePermanentlyModalStore(injectedDeps, {
      onDelete: () => this.store.navigation.tabs.closeTab({ tabId: this.tabId }),
    });
    this.addToCollectionModal = new AddToCollectionModalStore(injectedDeps, {});
    this.shareSheetModal = ShareSheetModalStore.forAppStore({ appStore: this.store });
    this.fileUploadRejectedModal = new FileUploadRejectedModalStore(injectedDeps);
    this.imageUploadRejectedModal = new ImageUploadRejectedModalStore(injectedDeps);
    this.pdfPageLimitWarningModal = new PdfPageLimitWarningModalStore();
    this.renameFileModal = new RenameFileModalStore(injectedDeps);

    makeObservable<
      this,
      | "resolveNoteObservable"
      | "lastNoteIdAddedToRecents"
      | "addNoteToRecents"
      | "itemObservableWasDeleted"
      | "setCurrentNoteWasDeletedBySync"
    >(this, {
      tabId: observable,
      noteId: observable,
      setNoteId: action,

      openFileUploadRejectedModal: true,
      openImageUploadRejectedModal: true,
      openPdfPageLimitWarningModal: true,
      openRenameFileModal: true,
      findInNote: true,
      fileUploadRejectedModal: false,
      imageUploadRejectedModal: false,
      pdfPageLimitWarningModal: false,
      renameFileModal: false,
      deleteNotePermanentlyModal: false,
      shareSheetModal: false,
      leaveNoteMessageModal: false,
      addToCollectionModal: false,
      mountState: false,
      noteVersionHistory: false,
      useEffects: false,
      goToMention: false,
      onMentionMouseOver: false,
      onMentionMouseOut: false,
      amIMounted: computed,
      myAccount: computed,
      itemObservable: observable,
      couldAccessNoteOnMount: observable,
      noteHydrationState: observable,
      currentNoteWasDeletedBySync: observable,
      organizeMenuOpenRequestCount: observable,
      voiceCleanupPanelToggleRequestCount: observable,
      voiceCleanupChangesAcceptRequestCount: observable,
      lastNoteIdAddedToRecents: false,
      isUpToDate: computed,
      note: computed,
      itemExists: computed,
      showNotFound: computed,
      isLoading: computed,
      itemObservableWasDeleted: computed,
      resolveNoteObservable: false,
      addNoteToRecents: false,
      setCurrentNoteWasDeletedBySync: action,
      setNoteObservable: action,
      setCouldAccessNoteOnMount: action,
      hydrateNoteIfNeeded: action,
      requestOpenOrganizeMenu: action,
      consumeOpenOrganizeMenuRequest: action,
      requestToggleVoiceCleanupPanel: action,
      consumeToggleVoiceCleanupPanelRequest: action,
      requestAcceptVoiceCleanupChanges: action,
      consumeAcceptVoiceCleanupChangesRequest: action,
    });

    this.itemObservable = this.resolveNoteObservable(this.noteId);

    // We need this to be already observable to react to changes its observable props.
    this.leaveNoteMessageModal = new LeaveNoteMessageModalStore(this);

    // Instantiate page-scoped Note Version History store
    this.noteVersionHistory = new NoteVersionHistoryPageStore(injectedDeps);

    reaction(
      () => ({ note: this.note, amIMounted: this.amIMounted }),
      ({ note: maybeNote, amIMounted }, { note: previousNote }) => {
        if (maybeNote?.id !== previousNote?.id) {
          this.addNoteToRecents(maybeNote);
          (async () => {
            await maybeNote?.refreshNoteContentDocument();
            if (maybeNote?.id !== this.noteId) return;
          })();
        }
        const note = (() => {
          if (maybeNote) {
            this.setCurrentNoteWasDeletedBySync(false);
            return maybeNote;
          }
          if (!previousNote || previousNote.id !== this.noteId || !amIMounted) return;

          this.setCurrentNoteWasDeletedBySync(true);
          return createProxyWithIsDeleted(previousNote);
        })();
        this.setNoteObservable(note);
      }
    );

    // Close version history when switching to a different note within this tab
    reaction(
      () => this.noteId,
      noteId => {
        if (
          this.noteVersionHistory.currentNoteId &&
          this.noteVersionHistory.currentNoteId !== noteId
        ) {
          this.noteVersionHistory.closeNoteVersionHistory();
        }
      }
    );

    // Guard against opening version history for invalid/fresh notes
    reaction(
      () => this.itemObservable?.isNoteContentDocumentLoaded,
      isLoaded => {
        if (!isLoaded && this.noteVersionHistory.currentNoteId) {
          this.noteVersionHistory.closeNoteVersionHistory();
        }
      }
    );

    reaction(
      () => this.itemObservable?.canAccess,
      (canAccess, previousCanAccess) => {
        const couldAccessNoteOnMount = (() => {
          // Getting access later is fine.
          if (canAccess) return true;
          // Prefer the previous value if it's already set.
          if (typeof previousCanAccess === "boolean") return previousCanAccess;
          // Either undefined (_unknown_) or false.
          return canAccess;
        })();
        this.setCouldAccessNoteOnMount(couldAccessNoteOnMount);
      }
    );

    // Trigger hydration when accessing a note that doesn't exist locally
    reaction(
      () => ({
        noteId: this.noteId,
      }),
      async ({ noteId }, previous) => {
        // Only trigger on note ID changes when mounted
        if (!noteId) return;
        if (previous?.noteId === noteId) return;

        // Check if note doesn't exist locally and trigger hydration
        const note = await this.store.notes.getAsync(noteId);
        if (!note) {
          this.hydrateNoteIfNeeded(noteId);
          return;
        }

        // Reset hydration state if note exists locally
        runInAction(() => {
          this.noteHydrationState = asyncResultModule.setInitial();
        });
      }
    );
  }

  setNoteId(noteId: string) {
    if (noteId !== this.noteId) {
      this.setCurrentNoteWasDeletedBySync(false);
    }
    this.noteId = noteId;
    this.itemObservable = this.resolveNoteObservable(noteId);
  }

  private resolveNoteObservable(noteId: string) {
    return this.store.notes.getNoteObservableById({ noteId });
  }

  private addNoteToRecents(note?: INoteObservable) {
    if (!note) return;
    if (!note.isAvailable) return;
    if (this.lastNoteIdAddedToRecents === note.id) return;

    this.lastNoteIdAddedToRecents = note.id;
    note.addToRecents();
  }

  requestOpenOrganizeMenu() {
    this.organizeMenuOpenRequestCount += 1;
  }

  consumeOpenOrganizeMenuRequest() {
    this.organizeMenuOpenRequestCount = 0;
  }

  requestToggleVoiceCleanupPanel() {
    this.voiceCleanupPanelToggleRequestCount += 1;
  }

  consumeToggleVoiceCleanupPanelRequest() {
    this.voiceCleanupPanelToggleRequestCount = 0;
  }

  requestAcceptVoiceCleanupChanges() {
    this.voiceCleanupChangesAcceptRequestCount += 1;
  }

  consumeAcceptVoiceCleanupChangesRequest() {
    this.voiceCleanupChangesAcceptRequestCount = 0;
  }

  goToMention: INotesViewPageStore["goToMention"] = ({
    id,
    mentionKind,
    url,
    keys,
    historyVersion,
  }) => {
    this.store.navigation.goToMention({
      id,
      mentionKind,
      url,
      keys,
      activeTabId: this.tabId,
      historyVersion,
    });
  };

  onMentionMouseOver: INotesViewPageStore["onMentionMouseOver"] = () => {
    // show the mention hover
  };

  onMentionMouseOut: INotesViewPageStore["onMentionMouseOut"] = () => {
    // hide the mention hover
  };

  openFileUploadRejectedModal: INotesViewPageStore["openFileUploadRejectedModal"] = ({
    code,
    fileName,
  }) => {
    this.fileUploadRejectedModal.open({
      code,
      fileName,
    });
  };

  openPdfPageLimitWarningModal: INotesViewPageStore["openPdfPageLimitWarningModal"] = ({
    pageCount,
  }) => {
    return this.pdfPageLimitWarningModal.open({ pageCount });
  };

  openImageUploadRejectedModal: INotesViewPageStore["openImageUploadRejectedModal"] = ({
    code,
    imageName,
  }) => {
    this.imageUploadRejectedModal.open({
      code,
      imageName,
    });
  };

  openRenameFileModal: INotesViewPageStore["openRenameFileModal"] = params => {
    this.renameFileModal.open(params);
  };

  setNoteObservable(noteObservable?: INoteObservable) {
    this.itemObservable = noteObservable;
  }

  setCouldAccessNoteOnMount(couldAccessNoteOnMount?: boolean) {
    this.couldAccessNoteOnMount = couldAccessNoteOnMount;
  }

  private setCurrentNoteWasDeletedBySync(currentNoteWasDeletedBySync: boolean) {
    this.currentNoteWasDeletedBySync = currentNoteWasDeletedBySync;
  }

  async hydrateNoteIfNeeded(noteId: string) {
    // Don't hydrate if already hydrating
    if (this.noteHydrationState.loading) {
      return;
    }

    // Set loading state
    runInAction(() => {
      this.noteHydrationState = asyncResultModule.setLoading();
    });

    try {
      const success = await this.store.notes.fetch(noteId);
      const note = await this.store.notes.getAsync(noteId);

      runInAction(() => {
        this.noteHydrationState = asyncResultModule.setReady(success);
        this.setNoteObservable(note);
      });
    } catch (unknownError) {
      runInAction(() => {
        const error = unknownError as Error;
        this.noteHydrationState = asyncResultModule.setError(
          new BaseError({
            message: "[debug] [NotesViewPageStore.hydrateNoteIfNeeded] Failed to fetch note.",
            info: objectModule.safeErrorAsJson(error),
          })
        );
      });
    }
  }

  get amIMounted() {
    return this.mountState.isMounted;
  }

  get isUpToDate() {
    return this.store.sync.isUpToDate;
  }

  get myAccount() {
    return this.store.spaceAccounts.myPersonalSpaceAccount;
  }

  get note() {
    if (!this.mountState.isMounted) return;
    const noteId = this.noteId;
    if (!noteId) return;
    return this.store.notes.getNoteObservableById({ noteId });
  }

  get showNotFound() {
    if (!this.mountState.isMounted) return false;

    if (this.currentNoteWasDeletedBySync || this.itemObservableWasDeleted) return true;

    if (this.couldAccessNoteOnMount === false) return true;

    if (this.leaveNoteMessageModal.isModalOpen) return false;

    // Skip not found while hydrating
    if (this.noteHydrationState.loading) return false;
    return !this.itemExists;
  }

  get isLoading(): boolean {
    if (this.currentNoteWasDeletedBySync || this.itemObservableWasDeleted) return false;
    return this.noteHydrationState.loading;
  }

  get itemExists(): boolean {
    // Return true when it's unknown.
    if (!this.mountState.isMounted || !this.noteId) {
      return true;
    }

    if (this.currentNoteWasDeletedBySync || this.itemObservable?.isDeleted) {
      return false;
    }

    // Assume exists while we're hydrating
    if (this.noteHydrationState.loading) {
      return true;
    }

    const doesNotExist = this.store.notes.doesNotExist(this.noteId);
    return doesNotExist !== true;
  }

  useEffects() {
    this.mountState.useEffects();
    this.findInNote.useEffects();
    this.store.templates.initialize();
  }

  private get itemObservableWasDeleted() {
    return this.itemObservable?.isDeleted === true;
  }
}
